/* Biometric payroll tab — extracted from BiometricPunch.js (iter85).
 * Receives all state/callbacks via a single spread props bag. Every
 * `data-testid` is preserved so existing e2e specs keep working. */
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Fingerprint, ClockCounterClockwise, Trash, UserCircle, ArrowClockwise, ArrowsClockwise, CurrencyDollar, WarningCircle, DownloadSimple, ArrowSquareOut } from '@phosphor-icons/react';

export const BiometricPayrollTab = ({
  workHoursFrom, setWorkHoursFrom, workHoursTo, setWorkHoursTo,
  workHours, loadWorkHours, payrollLoading,
  notifyOnBulk, setNotifyOnBulk, generateBulk, bulkBusy,
  exportPayrollCsv, exportBank, bankBusy,
  downloadPayslip,
}) => (
  <>
          <Card className="border border-[#E2E8F0] shadow-none">
            <CardContent className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <Label>Du</Label>
                <input type="date" value={workHoursFrom} onChange={(e) => setWorkHoursFrom(e.target.value)}
                  className="mt-1 w-full h-10 rounded-md border border-[#E2E8F0] px-3 text-sm bg-white"
                  data-testid="payroll-from-date" />
              </div>
              <div>
                <Label>Au</Label>
                <input type="date" value={workHoursTo} onChange={(e) => setWorkHoursTo(e.target.value)}
                  className="mt-1 w-full h-10 rounded-md border border-[#E2E8F0] px-3 text-sm bg-white"
                  data-testid="payroll-to-date" />
              </div>
              <div className="flex items-end">
                <Button onClick={loadWorkHours} disabled={payrollLoading}
                  className="w-full bg-[#0052FF] hover:bg-[#0044D4]"
                  data-testid="payroll-apply-btn">
                  <ArrowClockwise size={14} className="mr-1" /> Calculer
                </Button>
              </div>
              <div className="flex items-end">
                <Button variant="outline" onClick={exportPayrollCsv}
                  disabled={workHours.length === 0}
                  className="w-full border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  data-testid="payroll-export-btn">
                  <DownloadSimple size={14} className="mr-1" /> Export CSV
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* iter56 — Bulk exports (ZIP / merged PDF / bank CSV) */}
          <Card className="border border-[#E2E8F0] shadow-none">
            <CardContent className="p-4">
              <h3 className="font-outfit font-semibold text-sm mb-2">
                Exports groupés — {workHoursFrom} → {workHoursTo}
              </h3>
              <p className="text-xs text-[#64748B] mb-3">
                Génère les bulletins pour tous les employés + fichiers pour virement bancaire.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <Button variant="outline" onClick={() => generateBulk('zip')} disabled={bulkBusy}
                  className="border-[#0052FF] text-[#0052FF] hover:bg-blue-50"
                  data-testid="bulk-zip-btn">
                  📦 ZIP bulletins
                </Button>
                <Button variant="outline" onClick={() => generateBulk('merged')} disabled={bulkBusy}
                  className="border-[#0052FF] text-[#0052FF] hover:bg-blue-50"
                  data-testid="bulk-merged-btn">
                  📄 PDF fusionné
                </Button>
                <Button variant="outline" onClick={() => exportBank('sepa')} disabled={bankBusy}
                  className="border-amber-400 text-amber-700 hover:bg-amber-50"
                  data-testid="bank-sepa-btn">
                  🏦 CSV SEPA
                </Button>
                <Button variant="outline" onClick={() => exportBank('uemoa')} disabled={bankBusy}
                  className="border-amber-400 text-amber-700 hover:bg-amber-50"
                  data-testid="bank-uemoa-btn">
                  🏦 CSV UEMOA
                </Button>
              </div>
              <p className="text-[10px] text-[#94A3B8] mt-2">
                L&apos;export bancaire n&apos;inclut que les employés ayant un IBAN renseigné dans leur fiche.
              </p>
              {/* iter57 — Auto-notify checkbox for bulk generation */}
              <label className="mt-3 flex items-center gap-2 text-xs text-[#334155] cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={notifyOnBulk}
                  onChange={(e) => setNotifyOnBulk(e.target.checked)}
                  className="h-3.5 w-3.5 accent-[#0052FF]"
                  data-testid="bulk-notify-checkbox"
                />
                <span>
                  <b>Notifier automatiquement les employés</b> (email + SMS avec lien de signature)
                  lors de la génération groupée
                </span>
              </label>
            </CardContent>
          </Card>

          <Card className="border border-[#E2E8F0] shadow-none">
            <CardContent className="p-0">
              {payrollLoading ? (
                <div className="p-8 text-center text-[#94A3B8]">Calcul…</div>
              ) : workHours.length === 0 ? (
                <div className="p-8 text-center text-[#94A3B8] italic">Aucun résultat. Sélectionnez une période et cliquez sur Calculer.</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employé</TableHead>
                      <TableHead>Catégorie</TableHead>
                      <TableHead className="text-right">Attendues (h)</TableHead>
                      <TableHead className="text-right">Régulières (h)</TableHead>
                      <TableHead className="text-right">Sup. (h)</TableHead>
                      <TableHead className="text-right">Taux</TableHead>
                      <TableHead className="text-right">Paie estimée</TableHead>
                      <TableHead className="text-center">Bulletin</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {workHours.map((w) => {
                      const totalWorked = (w.hours_regular || 0) + (w.hours_overtime || 0);
                      const ratio = w.expected_hours > 0 ? (totalWorked / w.expected_hours) : 0;
                      const ratioPct = Math.round(ratio * 100);
                      return (
                        <TableRow key={`${w.kind}-${w.employee_id}`} data-testid={`payroll-row-${w.employee_id}`}>
                          <TableCell>
                            <div className="font-medium">{w.employee_name}</div>
                            <div className="text-[10px] text-[#94A3B8]">{w.function || '—'}</div>
                          </TableCell>
                          <TableCell>
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                              {w.staff_type}
                            </span>
                          </TableCell>
                          <TableCell className="text-right font-mono">{w.expected_hours}</TableCell>
                          <TableCell className="text-right font-mono font-semibold">{w.hours_regular || 0}</TableCell>
                          <TableCell className="text-right font-mono font-semibold text-emerald-700">
                            {(w.hours_overtime || 0) > 0 ? `+${w.hours_overtime}` : '—'}
                          </TableCell>
                          <TableCell className="text-right">
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                              ratio >= 0.95 ? 'bg-emerald-100 text-emerald-700' :
                              ratio >= 0.7 ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }`}>{ratioPct}%</span>
                          </TableCell>
                          <TableCell className="text-right font-mono font-semibold text-emerald-700">
                            {w.estimated_pay.toLocaleString('fr-FR')} F
                          </TableCell>
                          <TableCell className="text-center">
                            <Button size="sm" variant="outline" onClick={() => downloadPayslip(w.kind, w.employee_id, w.employee_name)}
                              className="border-[#0052FF] text-[#0052FF] hover:bg-blue-50"
                              data-testid={`payslip-download-${w.employee_id}`}>
                              📄 PDF
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
  </>
);
